import { DialogTitle, DialogActions, useTheme } from '@mui/material';
import { useState, useEffect } from 'react';
import { useLoader } from '@/contexts/loader/LoaderContext';
import {
    PaymentsUser,
    UpdateUserBankAccountDetailsRequest
} from '@/models/payment';
import { Validator, useErrorHandling } from '@/utils/errorHandling';
import PaymentsAPIServiceV2, {
    UserResponse
} from '@/services/PaymentsServiceV2';
import {
    StyledDialog,
    StyledDialogContainer,
    StyledDialogTitle
} from '@/components/consent/StyledDialog';
import { SemiBold, SMBold, SMRegular } from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import { InputText } from '@/components/shared/InputText';
import {
    OutlinedSecondaryButton,
    PrimaryButton
} from '@/components/shared/Button';
import { useGetUserQuery } from '@/services/paymentsQuery';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';

type UpdateBankAccountDialogProps = {
    userId?: string;
    open: boolean;
    validator: Validator;
    setValidator: React.Dispatch<React.SetStateAction<Validator>>;
    onClose: () => void;
    onSubmit: (request: UpdateUserBankAccountDetailsRequest) => Promise<void>;
};

export const UpdateBankAccountDialog = (
    props: UpdateBankAccountDialogProps
) => {
    const [bankCode, setBankCode] = useState<string>('');
    const [bankAccountNumber, setBankAccountNumber] = useState<string>('');

    const [isNemKonto, setIsNemKonto] = useState<boolean>(false);
    // US-3052: pre-resolved payout destination string from BE (BBAN or IBAN,
    // already formatted). `null` means "no bank info / NemKonto / fail-closed"
    // — let the existing fallback path render unchanged.
    const [payoutDestinationDisplay, setPayoutDestinationDisplay] = useState<
        string | null
    >(null);
    const [user, setUser] = useState<UserResponse>();
    const { setLoading } = useLoader();

    const { backendErrorHandler } = useErrorHandling();

    const onSubmit = () => {
        if (!props.userId) return;

        props.onSubmit({
            bankCode: bankCode,
            bankAccountNumber: bankAccountNumber,
            userId: props.userId
        });
    };

    const { data, isPending, isError } = useGetUserQuery({
        userId: props.userId,
        enabled: !!props.userId && props.open
    });

    useEffect(() => {
        if (data) {
            let responseUser = data.data;
            setUser(responseUser);
            setBankCode(responseUser.bankCode);
            setBankAccountNumber(responseUser.bankAccountNumber);
            setIsNemKonto(responseUser?.isNemkonto!);
            setPayoutDestinationDisplay(
                responseUser.payoutDestinationDisplay ?? null
            );
        }
    }, [data]);

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(setLoader(isPending));
    }, [isPending]);

    useEffect(() => {
        if (isError) {
            backendErrorHandler('Error fetching users: ');
        }
    }, [isError]);

    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <StyledDialog open={props.open} onClose={props.onClose}>
            <StyledDialogContainer style={{ width: '400px' }}>
                <StyledDialogTitle>
                    <div className="col align-center">
                        <SMBold fontSize={'14px'}>
                            {t('edit_bank_details')}
                        </SMBold>

                        <p style={{ marginBottom: 0 }} className="text-center">
                            <SemiBold
                                className="inline"
                                color={theme.palette.text.disabled}
                            >
                                {user?.email}
                            </SemiBold>
                        </p>
                    </div>
                </StyledDialogTitle>
                {/*
                 * US-3052 bug-loop 1: BE owns "what to show" via
                 * `payoutDestinationDisplay`. When non-null, render verbatim
                 * regardless of the legacy wire-side `isNemkonto` flag — for
                 * EUR-only employees `isNemkonto=true` on the wire (BBAN is
                 * empty per US-3051's marker → IsValidBankAccount=false) but
                 * BE has resolved the IBAN. Falling back to the literal
                 * "NemKonto" string in that case violates AC1 + AC4.
                 *
                 * When `payoutDestinationDisplay === null`, fall through to
                 * the existing fallback paths (FE owns localisation):
                 *   - isNemKonto         → NemKonto / missing-CPR copy
                 *   - !isNemKonto        → editable BBAN entry (no bank yet)
                 */}
                {payoutDestinationDisplay !== null && (
                    <>
                        <div>
                            <InputText
                                value={payoutDestinationDisplay}
                                name="payout_destination"
                                label={t('payout_destination')}
                                disabled
                                spacing="10px 0"
                            />
                        </div>
                        <DialogActions
                            sx={{
                                justifyContent: 'center',
                                gap: 1,
                                paddingX: 0,
                                paddingTop: '12px'
                            }}
                        >
                            <PrimaryButton
                                fullWidth
                                size="large"
                                style={{ height: '44px' }}
                                onClick={props.onClose}
                            >
                                {t('close')}
                            </PrimaryButton>
                        </DialogActions>{' '}
                    </>
                )}

                {payoutDestinationDisplay === null && !isNemKonto && (
                    <>
                        {' '}
                        <div>
                            <InputText
                                value={bankCode}
                                name="bank_code"
                                label="Bank code"
                                onChange={(val) => {
                                    setBankCode(val.target.value);
                                    props.setValidator(
                                        props.validator.clearError('bankCode')
                                    );
                                }}
                                spacing="10px 0"
                            />

                            <InputText
                                value={bankAccountNumber}
                                name="bank_account_number"
                                label="Bank account number"
                                onChange={(val) => {
                                    setBankAccountNumber(val.target.value);
                                    props.setValidator(
                                        props.validator.clearError(
                                            'bankAccountNumber'
                                        )
                                    );
                                }}
                                spacing="10px 0"
                            />

                            <SMRegular
                                color={theme.palette.text.disabled}
                                textAlign={'center'}
                                marginY={2}
                            >
                                If you update the bank account details manually,
                                they will no longer be updated during import
                                from salary provider.
                            </SMRegular>
                        </div>
                        <DialogActions
                            sx={{
                                justifyContent: 'center',
                                gap: 1,
                                paddingX: 0,
                                paddingTop: '12px'
                            }}
                        >
                            <OutlinedSecondaryButton
                                fullWidth
                                color="secondary"
                                style={{
                                    height: '44px',
                                    borderRadius: '8px'
                                }}
                                onClick={props.onClose}
                            >
                                <SMBold textTransform={'none'}>
                                    {t('Cancel')}
                                </SMBold>
                            </OutlinedSecondaryButton>

                            <PrimaryButton
                                fullWidth
                                size="large"
                                style={{ height: '44px' }}
                                onClick={onSubmit}
                            >
                                Save
                            </PrimaryButton>
                        </DialogActions>{' '}
                    </>
                )}

                {payoutDestinationDisplay === null && isNemKonto && (
                    <>
                        <div>
                            <InputText
                                value={
                                    !user?.cprNumber
                                        ? 'NemKonto - Missing CPR number'
                                        : 'NemKonto'
                                }
                                name="bank_account_number"
                                label="Account details"
                                disabled
                                error={!user?.cprNumber}
                                spacing="10px 0"
                            />
                        </div>
                        <DialogActions
                            sx={{
                                justifyContent: 'center',
                                gap: 1,
                                paddingX: 0,
                                paddingTop: '12px'
                            }}
                        >
                            <PrimaryButton
                                fullWidth
                                size="large"
                                style={{ height: '44px' }}
                                onClick={props.onClose}
                            >
                                {t('close')}
                            </PrimaryButton>
                        </DialogActions>{' '}
                    </>
                )}
            </StyledDialogContainer>
        </StyledDialog>
    );
};
